import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import Breadcrumbs from '../components/navigation/Breadcrumbs';
import SectionTitle from '../components/ui/SectionTitle';
import ContactInfoCard from '../components/contact/ContactInfoCard';
import ContactForm from '../components/forms/ContactForm';
import WhatsAppFloatingButton from '../components/layout/WhatsAppFloatingButton';
import { contactInfo } from '../data/contactInfo';

// Import local image
import contactImg from '../assets/contact.png';

const Contact = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumbs */}
      
      {/* Hero Section */}
      <div className="relative bg-primary-blue min-h-[35vh] flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
          style={{ backgroundImage: `url(${contactImg})` }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center text-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Contact Us</h1>
              <p className="text-xl text-gray-300">We're here to help with your legal needs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info & Form Section */}
      <section className="py-16 md:py-24 -mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Left Column - Contact Information */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-primary-blue mb-6">
                We'd Love to Hear From You
              </h3>
              <p className="text-dark-gray mb-8">
                Whether you have a question about our services, pricing, or anything else, 
                our team is ready to answer all your questions. Fill out the form or reach 
                out to us directly through the contact information below.
              </p>

              {/* Phone Numbers */}
              <ContactInfoCard icon={Phone} title="Phone" bgColor="blue">
                <a 
                  href={`tel:${contactInfo.phone.primaryLink}`} 
                  className="hover:text-gold transition-colors duration-200 block"
                >
                  {contactInfo.phone.primary}
                </a>
                <span className="text-gray-500">{contactInfo.phone.telephone}</span>
              </ContactInfoCard>

              {/* Email */}
              <ContactInfoCard icon={Mail} title="Email" bgColor="gold">
                <a 
                  href={`mailto:${contactInfo.email.primary}`} 
                  className="hover:text-gold transition-colors duration-200 block"
                >
                  {contactInfo.email.primary}
                </a>
              </ContactInfoCard>

              {/* Office Address */}
              <ContactInfoCard icon={MapPin} title="Office Address 1" bgColor="gray">
                <p>{contactInfo.address.line1}</p>
                <p>{contactInfo.address.line2}</p>
                <p>{contactInfo.address.line3}</p>
                <p className="font-medium mt-2">
                  {contactInfo.address.city}, {contactInfo.address.state} - {contactInfo.address.pincode}
                </p>
                <p>
                  Open Days: {contactInfo.address.workingHours.days}
                </p>
                <p>
                  Open Time: {contactInfo.address.workingHours.time}
                </p>
              </ContactInfoCard>
             <ContactInfoCard icon={MapPin} title="Office Address 2" bgColor="gray">
                <p>{contactInfo.address2.line1}</p>
                <p>{contactInfo.address2.line2}</p>
                <p>{contactInfo.address2.line3}</p>
                <p className="font-medium mt-2">
                  {contactInfo.address2.city}, {contactInfo.address2.state} - {contactInfo.address2.pincode}
                </p>
                <p>
                  Open Days: {contactInfo.address2.workingHours.days}
                </p>
                <p>
                  Open Time: {contactInfo.address2.workingHours.time}
                </p>
              </ContactInfoCard>
              <ContactInfoCard icon={Phone} title="WhatsApp Support" bgColor="blue">
                <p>Chat with us directly on WhatsApp for quick assistance.</p>
                <p>Click the WhatsApp icon to start a conversation.</p>
                <p>Our team will respond quickly to assist you with your legal queries.</p>
              </ContactInfoCard>
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:mt-2">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

{/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-primary-blue mb-4">
              Visit Our Office
            </h3>
            <p className="text-dark-gray max-w-2xl mx-auto">
              Our office is conveniently located in Mira Road, Thane. 
              Feel free to visit us during business hours for in-person consultations.
            </p>
          </div>
          
          <div className="relative">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0123456789!2d72.8762!3d19.2812!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDE2JzQ4LjEiTiA3MsKwNTInNDguNCJF!5e0!3m2!1sen!2sin!4v1234567890`}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Office Location"
            ></iframe>
            
            {/* Get Directions Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  `${contactInfo.address.line1}, ${contactInfo.address.line2}, ${contactInfo.address.line3}, ${contactInfo.address.city}, ${contactInfo.address.state} ${contactInfo.address.pincode}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pointer-events-auto bg-gold hover:bg-dark-gold text-primary-blue font-semibold py-3 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <MapPin className="w-5 h-5" />
                <span>Get Directions</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <WhatsAppFloatingButton />
    </div>
  );
};

export default Contact;

